// src/components/quote/bookPreviewSpec.js
//
// Turns the quote calculator's selections into the geometry and colors the 3D preview draws.
//
// IMPORTANT: every lookup below is keyed by the OPTION ID the backend returns from
// /api/quotes/pricing — 'hardcover_jacket', 'couche_130', 'wire_o' and friends. The design
// mock keyed its equivalents off the option's numeric price ("if coverAddon >= 3, draw a
// hardcover"), which silently mis-draws the book the moment an admin edits a price in
// AdminPricingPanel. Ids are stable; prices are not.
//
// Every map falls back to its first/plainest entry, so an option an admin adds later
// (a new binding, a new paper stock) renders as a plain book instead of crashing.

// Trim size in inches, drawn at PX_PER_INCH so the shapes stay proportional to each other:
// a 1/4 Letter really does look half the height of a Letter.
const SIZE_INCHES = {
  quarter_letter: [4.25, 5.5],
  half_letter: [5.5, 8.5],
  '6x9': [6, 9],
  letter: [8.5, 11],
  custom: [6, 9], // no dimensions to go on — draw the most common trim
};
const PX_PER_INCH = 15;
const DEFAULT_SIZE = SIZE_INCHES.half_letter;

const COVER_STYLES = {
  softcover: { cover: '#F5F3FB', spine: '#B8B1DD', title: '#413B8D', spineLabel: '#1A1A1A' },
  softcover_flaps: { cover: '#F5F3FB', spine: '#B8B1DD', title: '#413B8D', spineLabel: '#1A1A1A', flaps: true },
  hardcover: { cover: '#413B8D', spine: '#2A2559', title: '#F5F3FB', spineLabel: '#FFFFFF' },
  hardcover_jacket: { cover: '#2A2559', spine: '#1A1A1A', title: '#F5D76E', spineLabel: '#F5D76E' },
};

const PAPER_STYLES = {
  bond_75: { face: '#F7F2E6', edge: '#E3DBC4' },
  cultural_90: { face: '#F6EFDA', edge: '#DED2AE' },
  couche_130: { face: '#FFFFFF', edge: '#E8EAF5' },
};

// A diagonal highlight across the front cover. The finish only changes how hard it is.
const LAMINATION_SHEEN = {
  none: 'none',
  matte: 'linear-gradient(115deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.02) 45%, rgba(255,255,255,0) 70%)',
  glossy: 'linear-gradient(115deg, rgba(255,255,255,0.62) 0%, rgba(255,255,255,0.08) 38%, rgba(255,255,255,0) 66%)',
  soft_touch: 'linear-gradient(115deg, rgba(255,255,255,0.34) 0%, rgba(255,255,255,0.06) 55%, rgba(255,255,255,0) 80%)',
};

// Interior color reproduction, applied to the spread's illustration.
const PRINT_FILTERS = {
  bw: 'grayscale(1)',
  mixed: 'grayscale(0.55)',
  color: 'none',
};

const SPIRAL_HOLE = (y) => `radial-gradient(circle at 50% ${y}%, #1A1A1A 0 3px, transparent 3px)`;

function spinePattern(bindingId) {
  switch (bindingId) {
    case 'saddle_stitch':
      // Two staples through the fold.
      return 'repeating-linear-gradient(180deg, transparent 0 38%, #D4AF37 38% 41%, transparent 41% 59%, #D4AF37 59% 62%, transparent 62% 100%)';
    case 'spiral':
      return [10, 30, 50, 70, 90].map(SPIRAL_HOLE).join(', ');
    case 'wire_o':
      // Twin-loop wire reads as a run of squared-off bars rather than round holes.
      return 'repeating-linear-gradient(180deg, transparent 0 7px, rgba(26,26,26,0.6) 7px 13px)';
    default:
      return 'none'; // perfect binding — a plain glued spine
  }
}

// Sample cover art: the owner's "No Swimming" turtle from the design mock, on the front
// cover and on the left page of the open-book spread (greyed there for black & white
// print), as the mock has it. Served from public/images as a 20KB file. The mock inlined
// the same picture twice as a ~200KB base64 string, which is why the first build used a
// drawn placeholder instead.
export const COVER_ART = '/images/quote-cover-turtle.jpg';

// Sample interior copy for the open-book spread.
export const SPREAD_TITLE = 'A quiet method';
export const SPREAD_BODY =
  'She had learned to work the way water works on stone — not all at once, and never by ' +
  'force. Each morning the manuscript waited where she had left it, and each morning she ' +
  'added a little more, until the shape she had been circling for a year finally held still ' +
  'long enough to be written down.';

/**
 * @param {object} selections  the calculator's current selections, keyed by category id
 * @returns {object} geometry (in px) and colors for the six faces and the spread
 */
export default function buildBookSpec(selections = {}) {
  const [inW, inH] = SIZE_INCHES[selections.size] || DEFAULT_SIZE;
  const width = Math.round(inW * PX_PER_INCH);
  const height = Math.round(inH * PX_PER_INCH);

  // Spine thickness tracks the page count across the backend's 20–1000 range.
  const pages = Number(selections.pages) || 200;
  const depth = Math.max(9, Math.min(58, 9 + (pages / 1000) * 58));

  const cover = COVER_STYLES[selections.cover_type] || COVER_STYLES.softcover;
  const paper = PAPER_STYLES[selections.paper_type] || PAPER_STYLES.bond_75;

  return {
    width,
    height,
    depth,
    pages,
    coverFill: cover.cover,
    spineFill: cover.spine,
    titleFill: cover.title,
    spineLabelFill: cover.spineLabel,
    // Softcover-with-flaps gets a crease line where the flap folds in behind the cover.
    flapCrease: cover.flaps
      ? 'linear-gradient(90deg, transparent 0 88%, rgba(0,0,0,0.14) 88% 89%, transparent 89% 100%)'
      : null,
    sheen: LAMINATION_SHEEN[selections.lamination] ?? LAMINATION_SHEEN.none,
    spinePattern: spinePattern(selections.binding),
    paperFace: paper.face,
    paperEdge: paper.edge,
    printFilter: PRINT_FILTERS[selections.print_type] ?? PRINT_FILTERS.bw,
  };
}
